import React from 'react';

import Icon from '../../Icon/Icon';
import {
  RecorderActionConfig,
  RecorderFilter,
  RecorderLogFilter,
  RecorderRoute,
} from './viewModels';

interface Props {
  activeFilter: RecorderFilter;
  activeLogFilter: RecorderLogFilter;
  activeRoute: RecorderRoute;
  actionConfigs: Record<string, RecorderActionConfig>;
  actionStates: Record<string, boolean>;
  filterCounts: Record<'recording' | 'paused' | 'standby' | 'conflict', number>;
  logFilterCounts: Record<'scheduled' | 'paused' | 'conflict', number>;
  getStatusClass: (status: RecorderFilter) => string;
  onActionClick: (action: string) => void;
  onAddSlot: () => void;
  clearCount: number;
  onFilterChange: (status: RecorderFilter) => void;
  onLogFilterChange: (filter: RecorderLogFilter) => void;
  onRouteChange: (route: RecorderRoute) => void;
  recordNowCount: number;
  stopCount: number;
  routeConfigs: Record<RecorderRoute, RecorderActionConfig>;
}

const actionButtonStyles: Record<string, React.CSSProperties> = {
  record: { backgroundColor: '#da1e28' },
  schedule: { backgroundColor: '#0f62fe' },
  pause: { backgroundColor: '#f2c21c', color: '#000' },
  stop: { backgroundColor: '#000', color: '#fff', borderColor: '#da1e28' },
  clear: { backgroundColor: '#000', color: '#fff', borderColor: '#da1e28' },
};

const filterIconConfigs: Record<Exclude<RecorderFilter, null>, { icon: React.ComponentProps<typeof Icon>['type']; color: string }> = {
  recording: { icon: 'circle', color: '#da1e28' },
  paused: { icon: 'control_pause', color: '#f2c21c' },
  standby: { icon: 'timer', color: '#8d8d8d' },
  conflict: { icon: 'warning', color: '#da1e28' },
};

const logFilterIconConfigs: Record<Exclude<RecorderLogFilter, null>, { icon: React.ComponentProps<typeof Icon>['type']; color: string; statusClass: string }> = {
  scheduled: { icon: 'timer', color: '#8d8d8d', statusClass: 'progress-default' },
  paused: { icon: 'control_pause', color: '#f2c21c', statusClass: 'progress-warning' },
  conflict: { icon: 'warning', color: '#da1e28', statusClass: 'progress-danger' },
};

const RecorderToolbar: React.FC<Props> = ({
  activeFilter,
  activeLogFilter,
  activeRoute,
  actionConfigs,
  actionStates,
  filterCounts,
  logFilterCounts,
  getStatusClass,
  onActionClick,
  onAddSlot,
  clearCount,
  onFilterChange,
  onLogFilterChange,
  onRouteChange,
  recordNowCount,
  stopCount,
  routeConfigs,
}) => (
  <div className="status-filters">

    {/* Actions — hidden on logs view */}
    {activeRoute !== 'logs' && (
      <>
        <div
          className="action-box action-new"
          style={{ backgroundColor: '#6929c4' }}
          onClick={onAddSlot}
        >
          <Icon type="add" color="#fff" size={16} />
          <div style={{ fontSize: 11, marginTop: 2 }}>Add Slot</div>
        </div>
        {Object.entries(actionConfigs).map(([action, config]) => (
          <div
            key={action}
            className={`action-box ${config.className} ${actionStates[action] ? 'disabled' : ''}`}
            style={actionStates[action] ? { backgroundColor: '#6f6f6f', color: '#a8a8a8' } : actionButtonStyles[action]}
            onClick={() => { if (!actionStates[action]) onActionClick(action); }}
          >
            <Icon type={config.icon} color={actionStates[action] ? '#a8a8a8' : (action === 'pause' ? '#000' : '#fff')} size={16} />
            <div style={{ fontSize: 13, marginTop: 2 }}>
              {action === 'record'
                ? `Record Now ( ${recordNowCount} )`
                : action === 'stop'
                  ? `Stop ( ${stopCount} )`
                  : action === 'clear'
                    ? `Clear ( ${clearCount} )`
                    : config.title}
            </div>
          </div>
        ))}
        <div style={{ width: 1, alignSelf: 'stretch', background: '#444', margin: '0 2px' }} />
      </>
    )}

    {/* Filters */}
    {activeRoute === 'logs' ? (
      (Object.keys(logFilterIconConfigs) as Exclude<RecorderLogFilter, null>[]).map((logStatus) => {
        const cfg = logFilterIconConfigs[logStatus];
        return (
          <div
            key={logStatus}
            className={`status-box ${cfg.statusClass} ${activeLogFilter === logStatus ? 'active' : ''}`}
            style={{ borderWidth: activeLogFilter === logStatus ? 3 : 1 }}
            onClick={() => onLogFilterChange(activeLogFilter === logStatus ? null : logStatus)}
          >
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Icon type={cfg.icon} color={cfg.color} size={16} />
              <span>{logStatus.charAt(0).toUpperCase() + logStatus.slice(1)}</span>
            </div>
            <div style={{ marginTop: 4 }}>{logFilterCounts[logStatus]}</div>
          </div>
        );
      })
    ) : (
      (['recording', 'paused', 'standby', 'conflict'] as Exclude<RecorderFilter, null>[]).map((status) => (
        <div
          key={status}
          className={`status-box ${getStatusClass(status)} ${activeFilter === status ? 'active' : ''}`}
          style={{ borderWidth: activeFilter === status ? 3 : 1 }}
          onClick={() => onFilterChange(activeFilter === status ? null : status)}
        >
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Icon type={filterIconConfigs[status].icon} color={filterIconConfigs[status].color} size={16} />
            <span>{status === 'standby' ? 'Stand-by' : status.charAt(0).toUpperCase() + status.slice(1)}</span>
          </div>
          <div style={{ marginTop: 4 }}>{filterCounts[status]}</div>
        </div>
      ))
    )}

    {/* Views — pushed to right */}
    <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
      <div style={{ width: 1, alignSelf: 'stretch', background: '#444', margin: '0 2px' }} />
      {Object.entries(routeConfigs).map(([route, config]) => (
        <div
          key={route}
          className={`action-box ${config.className}`}
          style={{
            opacity: activeRoute === route ? 1 : 0.5,
            outline: activeRoute === route ? '2px solid #ffffff' : '2px solid transparent',
            outlineOffset: 2,
          }}
          onClick={() => onRouteChange(route as RecorderRoute)}
        >
          <Icon type={config.icon} color="white" size={16} />
          <div style={{ fontSize: 11, marginTop: 2 }}>{config.title}</div>
        </div>
      ))}
    </div>

  </div>
);

export default RecorderToolbar;
